import { useState, useEffect, useCallback } from 'react';
import axiosInstance, { extractErrorMessage } from '../../../services/axiosConfig';

/**
 * Interface para estatísticas do usuário
 */
export interface UserStats {
  conversations: number;
  projects: number;
}

/**
 * Hook para buscar estatísticas do usuário
 *
 * Busca dados de conversas, projetos e artefatos do usuário
 *
 * @returns Estado e funções para gerenciar estatísticas
 *
 * @example
 * ```tsx
 * const { stats, loading, error } = useUserStats();
 *
 * if (loading) return <LoadingSpinner />;
 * if (error) return <ErrorMessage>{error}</ErrorMessage>;
 *
 * return <div>Conversas: {stats?.conversations}</div>;
 * ```
 */
export const useUserStats = () => {
  const [stats, setStats] = useState<UserStats | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axiosInstance.get('/users/stats');

      // Extract data from BaseResponse structure
      if (response.data?.success && response.data?.data) {
        setStats(response.data.data);
      } else {
        throw new Error('Formato de resposta inválido');
      }
    } catch (err: unknown) {
      const errorMessage = extractErrorMessage(
        err instanceof Error ? err : new Error(String(err)),
        'Não foi possível carregar as estatísticas do usuário'
      );
      setError(errorMessage);
      console.error('Error fetching user stats:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return {
    stats,
    loading,
    error,
    refetch: fetchStats,
  };
};
